import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, PlayCircle, Sparkles, Mail, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { PORTRAIT_URL, RIBBON_URL, HERO_TEXTURE_1, HERO_TEXTURE_2 } from "@/lib/assets";

export default function Home() {
  const [data, setData] = useState({ featured_five_things: null });

  useEffect(() => { api.get("/content/home").then(r => setData(r.data)).catch(() => {}); }, []);

  return (
    <div>
      {/* HERO — editorial split */}
      <section className="relative overflow-hidden bg-cream">
        {/* Cross-fading Courtyard Park background textures */}
        <div className="hero-texture hero-texture-a" style={{ backgroundImage: `url(${HERO_TEXTURE_1})` }} aria-hidden />
        <div className="hero-texture hero-texture-b" style={{ backgroundImage: `url(${HERO_TEXTURE_2})` }} aria-hidden />
        <div className="absolute inset-0 bg-gradient-to-b from-cream/70 via-cream/40 to-cream/80 pointer-events-none" aria-hidden />
        <div className="brand-container grid lg:grid-cols-12 gap-10 items-center py-16 md:py-24 relative">
          <div className="lg:col-span-6 animate-in">
            <div className="brand-eyebrow !text-burgundy mb-6">The Honorable Judge Eileen Olds (Ret.)</div>
            <h1 className="brand-h1">
              <span className="script-auntie !text-burgundy text-6xl md:text-7xl lg:text-8xl align-baseline">Auntie</span>{" "}
              to a Generation<br/>in Need.
            </h1>
            <p className="brand-lede mt-8 max-w-lg">
              A membership home for wisdom, warmth, and the quiet authority of the bench —
              built for the folks who still show up for one another.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/about" className="btn-primary" data-testid="hero-meet-auntie-btn">
                Meet Judge Auntie™ <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="mt-14 grid grid-cols-3 gap-6 max-w-md">
              <Stat n="3" label="Decades on the bench" />
              <Stat n="3" label="Membership tiers" />
              <Stat n="Live" label="Monthly sessions" />
            </div>
          </div>

          <div className="lg:col-span-6 relative animate-in">
            <div className="relative overflow-hidden rounded-sm">
              <img src={PORTRAIT_URL} alt="Judge Eileen Olds portrait"
                className="w-full h-[560px] object-cover grayscale-0 contrast-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-burgundy/35 via-transparent to-transparent" />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-plum text-cream px-6 py-5 max-w-xs shadow-xl">
              <div className="brand-eyebrow !text-gold">Judge Auntie's™ Voice</div>
              <p className="font-serif text-xl mt-2 italic leading-snug">
                "Come sit with Judge Auntie™. There's a chair for you."
              </p>
              <div className="mt-3 text-xs uppercase tracking-widest text-gold/80">— Judge Auntie™</div>
            </div>
          </div>
        </div>
      </section>

      {/* Mailing list — slim band, right under hero */}
      <MailingListBand />

      {/* Featured Five Things */}
      {data.featured_five_things && (
        <section className="bg-plum text-cream">
          <div className="brand-container py-20 grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-5 order-2 lg:order-1">
              <div className="brand-eyebrow !text-gold mb-4"><Sparkles className="inline w-4 h-4 mr-1" /> Featured Series</div>
              <h2 className="font-serif text-4xl md:text-5xl leading-tight text-cream">{data.featured_five_things.title}</h2>
              <p className="mt-4 text-cream/70">{data.featured_five_things.short_description}</p>
              <ol className="mt-8 space-y-3">
                {(data.featured_five_things.takeaways || []).slice(0,5).map((t, i) => (
                  <li key={i} className="flex gap-4">
                    <span className="font-serif text-gold text-2xl leading-none">{String(i+1).padStart(2,"0")}</span>
                    <span className="text-cream/85 leading-relaxed">{t}</span>
                  </li>
                ))}
              </ol>
              <Link to={`/five-things/${data.featured_five_things.id}`} className="mt-10 btn-outline !border-gold !text-cream hover:!bg-plum-dark inline-flex" data-testid="home-featured-fivethings-link">
                {data.featured_five_things.full_video_url ? "Watch this episode" : "Read the full series"} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="lg:col-span-7 order-1 lg:order-2">
              <div className="relative rounded-lg overflow-hidden border border-gold/40">
                <img src={data.featured_five_things.cover_image || RIBBON_URL} alt="" className="w-full h-[420px] object-cover" />
                {data.featured_five_things.full_video_url && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <PlayCircle className="w-16 h-16 text-cream/90" />
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Testimonials — Voices from the Table */}
      <section className="bg-cream-alt border-y border-gold-light">
        <div className="brand-container py-20">
          <div className="brand-eyebrow">Voices from the Table</div>
          <h2 className="brand-h2 mt-3 mb-12">What folks say after they've sat with Judge Auntie™.</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { q: "Judge Olds speaks with the calm authority of the bench and the warmth of an auntie you actually listen to. Our audience left different than they arrived.", by: "Rev. Angela Whitfield", role: "Faith conference host" },
              { q: "Twenty minutes with Judge Auntie™ was more useful than the last three leadership seminars I sat through. She tells the truth — kindly.", by: "Marcus Reeves", role: "Nonprofit director" },
              { q: "She's the elder every community needs at the table. Rigor, humor, and a willingness to say the quiet thing out loud.", by: "Dr. Simone Barrett", role: "University dean" },
            ].map((t, i) => (
              <figure key={i} className="editorial-card p-8" data-testid={`home-testimonial-${i}`}>
                <div className="font-serif text-4xl text-gold leading-none">"</div>
                <blockquote className="mt-2 font-serif text-lg text-charcoal/85 leading-relaxed">{t.q}</blockquote>
                <figcaption className="mt-6 pt-4 border-t border-gold-light">
                  <div className="font-serif text-plum">{t.by}</div>
                  <div className="text-xs text-charcoal/60 uppercase tracking-widest mt-1">{t.role}</div>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

function MailingListBand() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      await api.post("/newsletter/subscribe", { email: email.trim(), name: "" });
      setDone(true);
      setEmail("");
      toast.success("You're on the list — Auntie will be in touch.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="bg-cream border-b border-gold-light" data-testid="home-mailing-list-band">
      <div className="brand-container py-8">
        <div className="flex flex-col md:flex-row md:items-center gap-6 md:gap-10">
          <div className="flex items-center gap-3 md:flex-shrink-0">
            <div className="w-10 h-10 rounded-full bg-plum/10 border border-plum/20 flex items-center justify-center">
              <Mail className="w-4 h-4 text-plum" />
            </div>
            <div>
              <div className="brand-eyebrow !text-plum">Join the Mailing List</div>
              <div className="text-sm text-charcoal/70 mt-0.5">Monthly notes from Judge Auntie™ — new episodes, events, and a word of wisdom.</div>
            </div>
          </div>
          <form onSubmit={submit} className="flex-1 flex items-center gap-2 w-full md:max-w-xl md:ml-auto" data-testid="home-mailing-list-form">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              disabled={busy || done}
              data-testid="home-mailing-list-email"
              className="flex-1 rounded-full border border-gold-light bg-cream-alt px-5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold focus:border-gold disabled:opacity-50"
            />
            <button
              type="submit"
              disabled={busy || done}
              data-testid="home-mailing-list-submit"
              className="btn-primary !py-2 !px-5 text-sm whitespace-nowrap"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : done ? "Added ✓" : "Join"}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}

function Stat({ n, label }) {
  return (
    <div>
      <div className="font-serif text-3xl text-plum">{n}</div>
      <div className="text-xs uppercase tracking-widest text-charcoal/60 mt-1">{label}</div>
    </div>
  );
}
